import { Fragment, memo } from 'react'

import { GameId, StoreOfferProduct, SupportedLanguage } from '@common'
import cx from 'classnames'
import Image from 'next/image'

import { useTranslatable } from '@/localization/useTranslatable'

import { useSnowplowProductCardViewImpression } from '../../../../utils/snowplow'
import { ProductsContainer } from '../structure/ProductsContainer'
import { StoreTypography } from '../typography/StoreTypography'
import { BrawlCardProduct } from './BrawlCardProduct'
import { CardOfferContainer } from './CardOfferContainer'
import { CardProduct } from './CardProduct'

import styles from './cardOffer.module.scss'

type BundleProps = {
  children?: React.ReactNode
  game: GameId
  product: StoreOfferProduct
}

const shouldHideCardTitle = (product: StoreOfferProduct) => {
  if (product.contents.length > 1) {
    return false
  }

  const titleKey = Object.keys(product.title)[0] as SupportedLanguage
  const productTitle = product.title[titleKey]
  const contentTitle = product.contents[0].product.title[titleKey]

  if (!productTitle || !contentTitle) {
    return false
  }

  return productTitle.localeCompare(contentTitle) === 0
}

export const CardOffer = memo(({ game, product }: BundleProps) => {
  const productCardSize =
    product.contents.length > 6 ? 'size-bundle-xs' : product.contents.length > 2 ? 'size-bundle-sm' : 'size-bundle-lg'

  const brawl = game === 'brawlstars'
  const brawlSingleOffer = brawl && product.contents.length === 1
  const flare = game !== 'clashroyale'

  const snowplowViewImpressionRef = useSnowplowProductCardViewImpression<HTMLDivElement>(
    product,
    'CardOffer',
    'CardOffer',
  )

  return (
    <CardOfferContainer
      offerType="offer"
      product={product}
      flare={flare}
      brawlSingleOffer={brawlSingleOffer}
      ref={snowplowViewImpressionRef}
    >
      {brawlSingleOffer ? (
        <BrawlSingleOfferProduct product={product} />
      ) : (
        <>
          <Heading product={product} />
          <ProductsContainer narrow={product.contents.length === 3} game={game}>
            {product.contents.map((content, i) => (
              <Fragment key={i}>
                {brawl ? (
                  <BrawlCardProduct
                    key={content.product.id}
                    size={productCardSize}
                    game={game}
                    content={content}
                    offerContent
                  />
                ) : (
                  <CardProduct
                    key={content.product.id}
                    offerType="offer"
                    size={productCardSize}
                    game={game}
                    content={content}
                    offerContent
                    hiddenTitle={shouldHideCardTitle(product)}
                  />
                )}
              </Fragment>
            ))}
          </ProductsContainer>
        </>
      )}
    </CardOfferContainer>
  )
})

CardOffer.displayName = 'CardOffer'

const Heading = memo(({ product }: { product: StoreOfferProduct }) => {
  const translatable = useTranslatable()
  return (
    <div className={cx(styles.offerHeading)}>
      <StoreTypography tag="h3" style="outlined" textSize="md" className={styles.title}>
        {translatable(product.title, 'title')}
      </StoreTypography>
    </div>
  )
})

Heading.displayName = 'Heading'

const BrawlSingleOfferProduct = memo(({ product }: { product: StoreOfferProduct }) => {
  const translatable = useTranslatable()
  const productImage = product.images[0]

  return (
    <div className={styles.brawSingleOffer}>
      <div className={cx(styles.offerHeading)}>
        <StoreTypography tag="h3" style="outlined" textSize="md" className={styles.title}>
          {translatable(product.title, 'title')}
        </StoreTypography>
        <StoreTypography tag="div" style="outlined" textSize="md" className={styles.title}>
          x{product.contents[0].amount}
        </StoreTypography>
      </div>
      <div className={styles.imageContainer}>
        <Image
          src={productImage}
          alt={translatable(product.title)}
          fill
          quality={100}
          sizes="200px"
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  )
})

BrawlSingleOfferProduct.displayName = 'BrawlSingleOfferProduct'
